import {
  BrowserMatcher,
  DeviceMatcher,
  DomainMatcher,
  InstantConfigInterpreter,
  InstantConfigLoader,
  InstantConfigOverrider,
  InstantConfigValue,
  RegionMatcher,
} from "@wikia/instant-config-loader";
import { inject, injectWithTransform, singleton } from "tsyringe";
import {
  AdsContext,
  Browser,
  Device,
  Geo,
  Logger,
  LoggerTransformer,
  Platform,
  QueryString,
  ServiceBaseUrl,
  isOdysseyEnabled,
} from "@identity/utils";
import { COOKIES, WINDOW } from "@identity/symbols";
import { CommunicationService } from "../CommunicationService";
import { SettingsRepositoryCookieImpl } from "./settings-repository-cookie-impl";

@singleton()
export class InstantConfigService {
  private repository: Dictionary<InstantConfigValue> = {};
  private readonly isLoadedByOdyssey: boolean;

  constructor(
    @inject(AdsContext) private readonly adsContext: AdsContext,
    @inject(Device) private readonly device: Device,
    @inject(Browser) private readonly browser: Browser,
    @inject(Platform) private readonly platform: Platform,
    @inject(Geo) private readonly geo: Geo,
    @inject(ServiceBaseUrl) private readonly serviceBaseUrl: ServiceBaseUrl,
    @inject(WINDOW) private readonly window: Window,
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @injectWithTransform(Logger, LoggerTransformer, "InstantConfig")
    private readonly logger: Logger,
  ) {
    this.isLoadedByOdyssey = isOdysseyEnabled(this.window);
  }

  async init(
    communicationService: CommunicationService,
    globals: Dictionary = {},
  ): Promise<InstantConfigService> {
    if (this.isLoadedByOdyssey) {
      this.repository = await this.loadConfigFromOdyssey();

      this.logger.info("instantiated with", this.repository);

      return this;
    }

    const env = this.adsContext.get()?.env;
    const instantConfigEndpoint = this.serviceBaseUrl.get().slice(0, -1);
    const appName = this.platform.get();
    const geo = this.geo.get();
    const instantConfigFallbackEndpoint = `https://script.wikia.nocookie.net/fandom-ae-assets/icbm/${
      env ?? "prod"
    }/icbm_state_${appName}.json`;
    const instantConfigLoader = new InstantConfigLoader({
      appName,
      instantConfigEndpoint,
      instantConfigFallbackEndpoint,
      compact: true,
    });
    const settingsRepository = new SettingsRepositoryCookieImpl(
      this.cookiesManager,
      communicationService,
      this.window,
    );
    const instantConfigInterpreter = new InstantConfigInterpreter(
      new BrowserMatcher(this.browser.getBrowser()),
      new DeviceMatcher(this.device.getDeviceType()),
      new DomainMatcher(),
      new RegionMatcher(geo),
      settingsRepository,
    );

    let config = await instantConfigLoader.getConfig();
    config = new InstantConfigOverrider().override(
      QueryString.getURLSearchParams(),
      config,
    );
    const interpreter = instantConfigInterpreter.init(config, globals);
    this.repository = interpreter.getValues();

    this.logger.info("instantiated with", this.repository);

    return this;
  }

  get<T extends InstantConfigValue>(key: string, defaultValue?: T): T {
    return (this.repository?.[key] as T) ?? defaultValue;
  }

  private loadConfigFromOdyssey(): Promise<Dictionary<InstantConfigValue>> {
    if (this.window.icbm?.config) {
      return Promise.resolve(this.window.icbm.config);
    }

    return new Promise<Dictionary<InstantConfigValue>>((resolve) => {
      this.window.addEventListener("instantConfigLoaded", () => {
        resolve(this.window.icbm!.config);
      });
    });
  }
}
